/**
 * Settings values, in words a person reads rather than the value we store.
 *
 * The Settings page is now a list of sentences (see `components/settings/
 * SettingRow`), so every stored value needs a reading. Keeping these here and
 * import-free means a node probe can check them (`npm run test:settings`) —
 * and the awkward ones are exactly the ones worth pinning: "1 things", a
 * 24-hour clock shown to someone who thinks in am/pm, and the difference
 * between a value a person chose and one we assumed.
 */

export const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const
export type DayKey = (typeof DAY_KEYS)[number]

const DAY_SHORT: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun',
}

/** "5" -> "5 things"; 1 is spelled, because "1 things" reads like a bug. */
export function countWord(n: number, one: string, many = `${one}s`): string {
  if (n === 1) return `one ${one}`
  return `${n} ${many}`
}

/** Minutes as a person says them: 60 -> "an hour", 90 -> "an hour and a half". */
export function minutesWord(mins: number): string {
  if (!Number.isFinite(mins) || mins <= 0) return 'no time'
  if (mins < 60) return `${mins} minutes`
  if (mins === 60) return 'an hour'
  if (mins === 90) return 'an hour and a half'
  if (mins % 60 === 0) return `${mins / 60} hours`
  return `${Math.floor(mins / 60)}h ${mins % 60}m`
}

/**
 * A stored 24-hour clock, read back in the viewer's own convention.
 * Never `new Date(hhmm)` — that is a date, not a wall clock.
 */
export function clockWord(hhmm: string, locale?: string): string {
  const m = /^(\d{1,2}):(\d{2})/.exec(hhmm || '')
  if (!m) return '—'
  const h = Number(m[1])
  const mi = Number(m[2])
  if (h > 23 || mi > 59) return '—'
  const d = new Date(2000, 0, 1, h, mi)
  try {
    return d.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' })
  } catch {
    return hhmm
  }
}

/** The working week, collapsed to the phrase a person would use. */
export function daysWord(days: readonly string[]): string {
  const set = days.map(d => String(d).slice(0, 3).toLowerCase())
  const known = DAY_KEYS.filter(d => set.includes(d))
  if (known.length === 0) return 'no fixed days'
  if (known.length === 7) return 'every day'
  const weekday = ['mon', 'tue', 'wed', 'thu', 'fri']
  if (known.length === 5 && weekday.every(d => known.includes(d as DayKey))) return 'weekdays'
  if (known.length === 2 && known.includes('sat') && known.includes('sun')) return 'weekends'
  return known.map(d => DAY_SHORT[d]).join(', ')
}

/** How far ahead planning reaches. */
export function horizonWord(key: string): string {
  const map: Record<string, string> = {
    today: 'just today',
    tomorrow: 'through tomorrow',
    next_3_days: '3 days ahead',
    next_7_days: 'a week ahead',
    next_14_days: 'two weeks ahead',
    next_30_days: 'a month ahead',
    next_90_days: 'three months ahead',
    next_365_days: 'a year ahead',
  }
  return map[key] || 'a week ahead'
}

/** How yesterday's unfinished work is raised. */
export function carryoverWord(mode: string): string {
  const map: Record<string, string> = {
    banner: 'show a banner',
    modal: 'ask me when I open Today',
    disabled: 'leave it alone',
  }
  return map[mode] || 'show a banner'
}

/**
 * The tone the motivator speaks in. These three are the whole vocabulary the
 * motivator knows (motivator-service/prompts.py); anything else is ignored
 * there, so the API refuses it.
 */
export const MOTIVATION_STYLES = [
  { value: 'supportive_best_friend', label: 'a supportive friend', blurb: 'Warm and enthusiastic. Believes in you.' },
  { value: 'calm_older_sibling', label: 'a calm older sibling', blurb: 'Grounded and steady. No fuss.' },
  { value: 'quiet_mentor', label: 'a quiet mentor', blurb: 'Thoughtful and unhurried. Builds confidence.' },
] as const

export function toneWord(style: string): string {
  return MOTIVATION_STYLES.find(s => s.value === style)?.label || 'a supportive friend'
}

/** Carryover of things already started: 0 means none are pulled in. */
export function carryInWord(allowed: boolean, most: number): string {
  if (!allowed || most <= 0) return 'no, start fresh'
  return `up to ${countWord(most, 'thing')}`
}

/**
 * The work-in-progress cap, read so it cannot be mistaken for the daily one.
 *
 * "3 things" appeared as the value of THREE different rows in this group —
 * things a day, blocks a day, and this — which made them look like the same
 * setting entered three times. This one is not a daily number at all: it counts
 * everything you have started and not finished, across every day.
 */
export function atATimeWord(n: number): string {
  return n === 1 ? 'one at a time' : `${n} at a time`
}

/** The eased-off limit on a hard day, read against the normal one. */
export function recoveryWord(enabled: boolean, limit: number, normal: number): string {
  if (!enabled) return 'no, keep the usual limit'
  const effective = Math.min(limit, normal)
  return `down to ${countWord(effective, 'thing')}`
}
